"use client";
/***
 *
 * @file components/Button/index.tsx
 * @description Button component will be used for rendering the content.
 * @author Naveen Kumar
 * @organization Ingka Centres
 * @since 13 Feb 2024
 *
 */

import React, { ButtonHTMLAttributes, MouseEventHandler, ReactNode, useEffect, useRef, useState } from "react";
// import "./Button.scss";
import * as classes from "./Button.css";
// import "../../assets/styles/form.scss";
import { StyledButton } from "@/shared/StyledComponents";
import classNames from "classnames";
import { T_KeyValue } from "@/types/app.type";
import { sendEventToGTM } from "@/helpers/gtm.helper";
import { getMeaningfulDomPath } from "@/helpers/utils.helper";
import { useDynamicAltText } from "@/hooks/useDynamicAltText";

interface ButtonProps {
  id?: string,
  data?: any,
  typeid?: string,
  className?: string,
  cssClass?: string,
  variant?: string,
  tabIndex?: number | 0,
  "aria-label"?: string | "",
  action?: MouseEventHandler<HTMLButtonElement>,
  flatSide?: string,
  size?: "m" | "l" | "s" | "xl";
  afterIcon?: any;
  style?: React.CSSProperties;
  hide?: Boolean;
  "data-cf-node-id"?: any;
  eventName?: string;
  location?: string;
  button_link?: string;
  GTMPayload?: any;
  GTMEvent?: string;
  GTMEnabled?: boolean;
}

type T_Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  children?: ReactNode,
  id?: string,
  data?: any,
  typeid?: string,
  cssClass?: string,
  variant?: string,
  tabIndex?: number | 0,
  "aria-label"?: string | "",
  action?: MouseEventHandler<HTMLButtonElement>,
  flatSide?: string,
  size?: "m" | "l" | "s" | "xl";
  afterIcon?: any;
  style?: React.CSSProperties;
  hide?: Boolean;
  btnpProps?: ButtonProps;
  button_link?: string;
  GTMLabel?: string;
  intent?: string;
}

/**
 * Description: Renders a button component
 * @param param0
 * @example Parameter breakup
 * ```
 *  param0: {
 *    children,          //children components to be rendered
 *    id: "" ,
 *    data: {},           // data object holds the content
 *    typeid: "",         // used for testing purpose
 *    onClick: () => {},  // callback onClick method
 *    type: ""            // represents button type
 *  }
 * ```
 * @example T_Props
 * ```
 * type T_Props = {
 *     id?: string,        // optional
 *     data?: any,          // optional
 *     typeid?: string,    //optional
 * }
 * ```
 * @returns a styled button component with an onClick method
 */

const Button = ({
  id = "button",
  hide = false,
  children,
  data = {},
  typeid = "",
  type = "button",
  cssClass = "",
  style = {},
  GTMLabel,
  intent = "",
  // onClick = (): any => { return null }, 
  onClick = () => { },
  ...rest }: T_Props) => {
  const elData = data;

  const btnRef = useRef<HTMLButtonElement>(null);
  const altText = useDynamicAltText(btnRef);

  const [GTMLocation, setGTMLocation] = useState<string>("NA");
  const [parentLink, setParentLink] = useState<HTMLAnchorElement | null>(null);

  useEffect(() => {
    if (btnRef.current) {
      const pLink = btnRef.current.closest("a");
      setParentLink(pLink);
    }
  }, []);

  useEffect(() => {
    if (!btnRef?.current) return;

    const defaultPath = getMeaningfulDomPath(btnRef?.current);
    const customPath = getMeaningfulDomPath(btnRef?.current, " / ");

    // console.log("DOM Path (default) - Button:", defaultPath);
    // console.log("DOM Path (custom) - Button:", customPath);

    setGTMLocation(
      rest?.btnpProps?.location || getMeaningfulDomPath(btnRef?.current, "/")
    );
  }, [btnRef, rest?.btnpProps?.location]);

  const onClickHandler = (event: any) => {
    if (rest?.action) {
      rest?.action(event);
    }
  }

  return (
    <>
      {/* <pre style={{color: "black"}}>id---{JSON.stringify(rest?.btnpProps)}</pre> */}
      {/* children -- {children} | 
      elData?.content?.caption -- {elData?.content?.caption} | 
      elData?.content?.buttonText -- {elData?.content?.buttonText} |  */}
      {/* <pre>{JSON.stringify(parentLink, null, 2)}</pre> */}
      {/* {parentLink} */}
      <StyledButton
        ref={btnRef}
        // {...rest?.btnpProps}
        data-cf-node-id={rest?.btnpProps?.['data-cf-node-id']}
        id={elData?.content?.id || id}
        data-testid={`button-${typeid}`}
        aria-label={`${rest?.["aria-label"] || (children as string) || elData?.content?.caption || elData?.content?.buttonText}`} // new 
        type={elData?.content?.type || type}
        // component="button" // new
        className={
          classNames(`${elData?.content?.variant === "link" ?
            "html-link" : "btn"} 
            btn-${elData?.content?.variant}`, elData?.title || '',
            // rest?.btnpProps?.['data-cf-node-id'] ? `cfstyles-${rest?.btnpProps?.['data-cf-node-id']}` : '',
            'rounded-button',
            elData?.content?.cssClass, cssClass, (rest?.variant || "primary"),
            "group font-buttonLabel relative outline-none",
            hide ? "hidden" : "",
            // (!Array.isArray(rest?.variant) && (classes.variant[rest?.variant], classes.focusVisible[rest?.variant])),
            classes?.variant[rest?.btnpProps?.variant || "primary"],
            // classes.variant[rest?.variant!],
            classes?.focusVisible[rest?.btnpProps?.variant || "primary"],
            // rest?.iconLightVariant && classes.lightAppearance[rest?.variant],
            [classes?.size[rest?.btnpProps?.size || "m"]],
            {
              "rounded-s-none focus-visible:after:rounded-s-none": rest?.flatSide === "left",
              "rounded-r-none focus-visible:after:rounded-r-none": rest?.flatSide === "right",
              // [classes?.size[rest?.size || "m"]]: typeof children === "string",
              // [classes.iconSize[rest?.size]]: typeof rest?.children !== "string",
            },
            `${elData ? (`${elData?.content?.variant === "link" ? "html-link" : "btn"}, btn btn-${elData?.content?.variant}`) : ''}`,
            elData?.title || '',
            elData?.content?.rounded ? 'rounded' : '',
            rest?.btnpProps?.className,
            cssClass,
          )}
        style={{ ...style, ...(elData?.content?.customStyles || {}) }}
        onClick={async (event) => {
          if (rest?.btnpProps?.GTMEnabled !== false && "CustomLink" !== parentLink?.getAttribute("data-testid")) {
            const payload = await (() => ({
              event: `Button ${rest?.btnpProps?.GTMEvent || "Click"}`,
              component: `Button`,
              componentId: elData?.content?.id || id,
              type: elData?.content?.type || type,
              label: children || elData?.content?.caption || elData?.content?.buttonText || GTMLabel || altText,
              pagePath: window.location.pathname,
              linkUrl: rest?.button_link || "NA",
              onClickEvent: rest?.btnpProps?.eventName || "NA",
              location: GTMLocation,
              ...(intent ? { clickIntent: intent } : {}),
              ...rest?.btnpProps?.GTMPayload
            }))()
            // console.log("GTMPayload ---- ", rest?.btnpProps?.GTMPayload)
            sendEventToGTM(payload)
          }
          const onClickExe: any = onClick(event);
          if (Object.keys(elData)?.length > 1 || onClickExe) {
            onClick(event);
          } else {
            onClickHandler(event);
          }
        }}
        tabIndex={rest?.tabIndex} // new
      >
        {children || elData?.content?.caption || elData?.content?.buttonText}
        {/* -- aaa:  {parentLink?.getAttribute("data-testid")} -- 
        {parentLink?.getAttribute("data-testid")} | GTMEnabled: 
        {JSON.stringify(rest?.btnpProps?.GTMEnabled)} | 
        {JSON.stringify("CustomLink" !== parentLink?.getAttribute("data-testid"))} | 
        {JSON.stringify(rest?.btnpProps?.GTMEnabled !== false && "CustomLink" !== parentLink?.getAttribute("data-testid"))} */}

        {/* -- <pre>{JSON.stringify(rest?.btnpProps, null, 2)}</pre> */}
        {/* === <pre>{JSON.stringify({event: `Button ${rest?.btnpProps?.GTMEvent || "Click"}`, 
              component: `Button`, 
              componentId: elData?.content?.id || id, 
              type: elData?.content?.type || type,
              label: children || elData?.content?.caption || elData?.content?.buttonText,
              pagePath: window.location.pathname, 
              linkUrl: rest?.button_link || "NA",
              onClickEvent: rest?.btnpProps?.eventName || "NA",
              location: GTMLocation,
              ...rest?.btnpProps?.GTMPayload}, null, 2)}</pre> */}
      </StyledButton>
    </>
  );
};

export default Button;




















// 'use client'
// /***
//  *
//  * @file components/Button/index.tsx
//  * @description Button component will be used for rendering the content.
//  * @author Naveen Kumar
//  * @organization Ingka Centres
//  * @since 13 Feb 2024
//  *
//  */

// import React, { ButtonHTMLAttributes, ReactNode } from "react";
// import "./Button.scss";
// import "../../assets/styles/form.scss"
// import { StyledButton } from "@/shared/StyledComponents";
// import classNames from "classnames";
// import { useTranslation } from 'react-i18next';
// import { IconProps } from "../Icons";


// type T_Props = ButtonHTMLAttributes<HTMLButtonElement> & {
//   children?: ReactNode,
//   id?: string,
//   data?: any,
//   typeid?: string,
//   text?: string,
//   cssClass?: string,
//   cssClass?: string,
//   value?: string,
//   variant?: string,
//   size?: string,
//   action?: (props: any) => void | undefined,
//   afterIcon?: (props: IconProps) => ReactNode | undefined
// }

// /**
//  * Description: Renders a button component
//  * @param param0
//  * @example Parameter breakup
//  * ```
//  *  param0: {
//  *    children,          //children components to be rendered
//  *    id: "" ,
//  *    data: {},           // data object holds the content
//  *    typeid: "",         // used for testing purpose
//  *    onClick: () => {},  // callback onClick method
//  *    type: ""            // represents button type
//  *  }
//  * ```
//  * @example T_Props
//  * ```
//  * type T_Props = {
//  *     id?: string,        // optional
//  *     data?: any,          // optional
//  *     typeid?: string,    //optional
//  * }
//  * ```
//  * @returns a styled button component with an onClick method
//  */


// const Button = ({ id = "button", children, data = {}, typeid = "", type = "button", text = "", cssClass = "", cssClass = "", onClick = () => { }, onMouseDown = () => { }, ...props }: T_Props) => {
//   const elData = useContentfulLiveUpdates(data);
//   const inspectorModeProps = useContentfulInspectorMode();

//   return (
//     <StyledButton
//       id={elData?.content?.id || id}
//       className={classNames(`${elData?.content?.variant === "link" ? "html-link" : "btn"} btn-${elData?.content?.variant}`, (elData?.title || ''), (elData?.content?.rounded ? 'rounded' : ''), elData?.content?.cssClass, cssClass, className)}
//       data-testid={`button-${typeid}`}
//       type={elData?.content?.type || type}
//       style={{ ...(elData?.content?.customStyles || {}) }}
//       onClick={onClick}
//       onMouseDown={onMouseDown}
//     >
//       {children || elData?.content?.caption || elData?.content?.buttonText || text || rest?.value}
//     </StyledButton>
//   );
// };

// export default Button;
